import { useCallback, useEffect, useState } from "react";
import { Link, NavLink, Outlet, Route, Routes, useLocation, useNavigate, useOutletContext as useOutlet } from "react-router-dom";
import { HardHat, FileText, MessageSquareWarning, FolderOpen, Receipt, Building2, Menu, X, AlertTriangle, CheckCircle2, Upload, LogOut, Bell, Trophy } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";
import { useEngine } from "@/engine/store";
import { euro, fmtDate, COMPANY_DOC_TYPES, INVOICE_TYPES, LOT_CATALOG, today } from "@/engine/model";
import { Chip, Bar, Gantt, ComplianceBadge, History, RoleBar, PhotoField, PhotoThumb } from "@/engine/shared";
import { Card, Field, inputCls, SectionTitle, Placeholder, Pill } from "@/partner/ui";
import { EchangesTab } from "@/partner/pages/ProjectEngineTabs";
import { useAuth } from "@/context/AuthContext";

const TERRE = "#8A5541";
const SABLE = "#D3A77C";
const REF = "VSL01-26-014";


const TBtn = ({ children, variant = "primary", className = "", testid, ...rest }) => (
  <button
    data-testid={testid}
    className={`inline-flex items-center justify-center gap-2 rounded-full px-4 py-2 text-sm font-bold transition-opacity disabled:cursor-not-allowed disabled:opacity-50 ${variant === "primary" ? "text-white hover:opacity-90" : "border bg-white text-navy hover:opacity-80"} ${className}`}
    style={variant === "primary" ? { backgroundColor: TERRE } : { borderColor: SABLE }}
    {...rest}
  >
    {children}
  </button>
);

const NAV = [
  { to: "/entreprise", label: "Mes chantiers", icon: HardHat, end: true, key: "chantiers" },
  { to: "/entreprise/devis", label: "Devis", icon: FileText, key: "devis" },
  { to: "/entreprise/observations", label: "Observations", icon: MessageSquareWarning, key: "observations" },
  { to: "/entreprise/documents", label: "Documents", icon: FolderOpen, key: "documents" },
  { to: "/entreprise/factures", label: "Factures", icon: Receipt, key: "factures" },
  { to: "/entreprise/mon-entreprise", label: "Mon entreprise", icon: Building2, key: "mon-entreprise" },
];

const useCompanyId = () => {
  const engine = useEngine();
  const [id, setId] = useState(() => localStorage.getItem("cozyhome_demo_company") || "pr2");
  if (engine.mode === "BACKEND") return [engine.currentEntityId, () => {}];
  return [id, (v) => { localStorage.setItem("cozyhome_demo_company", v); setId(v); }];
};

const Layout = ({ companyId, setCompanyId }) => {
  const engine = useEngine();
  const c = engine.company(companyId);
  const comp = engine.compliance(companyId);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const showDemoTools = engine.mode === "DEMO" && process.env.NODE_ENV !== "production";
  if (!c) {
    return <div data-testid="ent-company-loading" className="flex min-h-screen items-center justify-center bg-[#FAF6F1] text-sm font-semibold text-stone-500">Chargement de votre entreprise…</div>;
  }
  const Nav = ({ suffix = "" }) => (
    <nav className="flex flex-col gap-0.5">
      {NAV.map((n) => (
        <NavLink key={n.to} to={n.to} end={n.end} onClick={() => setOpen(false)} data-testid={`ent-nav-${n.key}${suffix}`}
          className={({ isActive }) => `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold transition-colors ${isActive ? "bg-white/15 text-white" : "text-white/70 hover:bg-white/10 hover:text-white"}`}>
          <n.icon className="h-[18px] w-[18px]" /> {n.label}
        </NavLink>
      ))}
    </nav>
  );
  const Brand = () => (
    <Link to="/entreprise" className="flex items-center gap-3" data-testid="ent-brand">
      <span className="flex h-10 shrink-0 items-center justify-center rounded-xl bg-cream px-2.5"><Logo className="h-7 w-auto" /></span>
      <span><span className="block font-display text-lg font-semibold leading-none text-white">CozyHome</span><span className="mt-1 block text-[10px] font-bold uppercase tracking-[0.24em]" style={{ color: SABLE }}>Espace Entreprise</span></span>
    </Link>
  );
  const Switcher = ({ suffix = "" }) => (
    <div className="rounded-xl bg-white/10 p-2.5" data-testid={`ent-company-switch${suffix}`}>
      <p className="px-1 text-[10px] font-bold uppercase tracking-[0.2em] text-white/50">{engine.mode === "BACKEND" ? "Session entreprise sécurisée" : "Démo · entreprise connectée"}</p>
      {showDemoTools && <select data-testid={`ent-company-select${suffix}`} value={companyId} onChange={(e) => setCompanyId(e.target.value)} className="mt-2 w-full rounded-lg bg-white px-2 py-1.5 text-xs font-bold text-navy">
        {Object.values(engine.state.companies).map((co) => <option key={co.id} value={co.id}>{co.name}</option>)}
      </select>}
      {engine.mode === "BACKEND" && <p data-testid={`ent-company-session${suffix}`} className="mt-2 text-xs font-bold text-white">{c.name}</p>}
    </div>
  );
  return (
    <div data-testid="entreprise-space" className="min-h-screen font-sans text-navy" style={{ backgroundColor: "#FAF6F1" }}>
      <aside data-testid="ent-sidebar" className="fixed inset-y-0 left-0 z-40 hidden w-64 flex-col px-4 py-6 lg:flex" style={{ backgroundColor: TERRE }}>
        <Brand />
        <div className="mt-8 flex-1"><Nav /></div>
        <Switcher />
        <p className="mt-3 px-1 text-xs font-semibold text-white">{c.name}</p>
        <p className="px-1 text-[11px] text-white/60">{c.trade}</p>
        {engine.mode === "BACKEND" && <button data-testid="ent-logout" onClick={async () => { await logout(); navigate("/acces-phase3"); }} className="mt-4 flex items-center gap-2 px-1 text-xs font-bold text-white/70 hover:text-white"><LogOut className="h-4 w-4" /> Déconnexion</button>}
      </aside>
      <header className="sticky top-0 z-40 flex h-16 items-center justify-between px-4 lg:hidden" style={{ backgroundColor: TERRE }}>
        <Brand />
        <button data-testid="ent-mobile-open" onClick={() => setOpen(true)} aria-label="Menu" className="rounded-full border border-white/30 p-2.5 text-white"><Menu className="h-5 w-5" /></button>
      </header>
      {open && (
        <div className="fixed inset-0 z-[60] lg:hidden">
          <div className="absolute inset-0 bg-navy/40" onClick={() => setOpen(false)} />
          <aside data-testid="ent-mobile-menu" className="absolute inset-y-0 left-0 flex w-[86%] max-w-xs flex-col overflow-y-auto px-4 py-5" style={{ backgroundColor: TERRE }}>
            <div className="flex items-center justify-between"><Brand /><button data-testid="ent-mobile-close" onClick={() => setOpen(false)} aria-label="Fermer" className="rounded-full border border-white/30 p-2 text-white"><X className="h-4 w-4" /></button></div>
            <div className="mt-6 flex-1"><Nav suffix="-mobile" /></div>
            <Switcher suffix="-mobile" />
            {engine.mode === "BACKEND" && <button data-testid="ent-logout-mobile" onClick={async () => { await logout(); navigate("/acces-phase3"); }} className="mt-4 flex items-center gap-2 text-xs font-bold text-white"><LogOut className="h-4 w-4" /> Déconnexion</button>}
          </aside>
        </div>
      )}
      <main className="lg:pl-64">
        <div className="mx-auto max-w-6xl px-4 pb-16 pt-6 sm:px-6 lg:px-10">
          <div className="mb-4 flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.2em] text-stone-400"><span style={{ color: TERRE }}>Espace Entreprise</span> · {NAV.find((n) => (n.end ? location.pathname === n.to : location.pathname.startsWith(n.to)))?.label}</div>
          {!comp.ok && (
            <div data-testid="ent-blocked-banner" className="mb-5 flex flex-col gap-2 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm sm:flex-row sm:items-center sm:justify-between">
              <p className="flex items-center gap-2 font-bold text-red-700"><AlertTriangle className="h-4 w-4" /> Intervention bloquée — documents à régulariser : {comp.missing.map((m) => m.label).join(", ")}</p>
              <Link to="/entreprise/documents" data-testid="ent-blocked-link" className="text-xs font-bold text-red-700 underline">Déposer mes documents</Link>
            </div>
          )}
          <RoleBar current="entreprise" ref={REF} />
          <Outlet context={{ companyId }} />
        </div>
      </main>
    </div>
  );
};

const Header = ({ title, sub }) => (
  <div className="mb-6"><h1 className="font-display text-3xl font-medium tracking-tight text-navy sm:text-4xl">{title}</h1>{sub && <p className="mt-1.5 text-sm text-stone-500">{sub}</p>}</div>
);

// ---------- Mes chantiers ----------
const Chantiers = ({ companyId }) => {
  const engine = useEngine();
  const p = engine.project(REF);
  const c = engine.company(companyId);
  const comp = engine.compliance(companyId);
  const myLots = p.lots.filter((l) => l.companyId === companyId);
  const [form, setForm] = useState({ lotId: myLots[0]?.id || "", declared: "", comment: "", photos: 0 });
  const submit = (e) => {
    e.preventDefault();
    if (form.declared === "" || !form.lotId) return toast.error("Indiquez le pourcentage d'avancement.");
    if (engine.declareProgress(REF, companyId, form.lotId, Number(form.declared), form.comment, Number(form.photos))) {
      toast.success("Avancement déclaré — en attente de vérification par le Partenaire.");
      setForm({ ...form, declared: "", comment: "", photos: 0 });
    }
  };
  return (
    <div data-testid="ent-chantiers">
      <Header title={`Bonjour, ${c.name}`} sub="Vos chantiers, vos interventions et ce que vous devez faire." />
      <Card className="overflow-hidden">
        <div className="h-1" style={{ backgroundColor: TERRE }} />
        <div className="p-5">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div><p className="font-mono text-[11px] font-bold" style={{ color: TERRE }}>{p.ref}</p><h2 className="font-display text-2xl font-medium text-navy">{p.name}</h2><p className="text-xs text-stone-500">{p.address} · Partenaire : {p.partner.company}</p></div>
            <ComplianceBadge companyId={companyId} testid="ent-compliance" />
          </div>
          <ul className="mt-4 grid gap-3 md:grid-cols-2" data-testid="ent-my-lots">
            {myLots.map((l) => (
              <li key={l.id} data-testid={`ent-lot-${l.id}`} className="rounded-xl border p-4 text-sm" style={{ borderColor: "#EADFD3" }}>
                <div className="flex items-center justify-between"><p className="font-semibold text-navy">{l.label}</p><Chip status={comp.ok ? (l.validatedProgress === 100 ? "cloture" : "en_cours") : "expire"} label={comp.ok ? (l.validatedProgress === 100 ? "Terminé" : "Planifiée") : "Bloquée"} /></div>
                <dl className="mt-2 grid grid-cols-2 gap-2 text-xs">
                  <div><dt className="text-stone-400">Début prévu</dt><dd className="font-semibold text-navy">{fmtDate(l.start)}</dd></div>
                  <div><dt className="text-stone-400">Fin prévue</dt><dd className="font-semibold text-navy">{fmtDate(l.end)}</dd></div>
                  <div><dt className="text-stone-400">Durée / effectif</dt><dd className="font-semibold text-navy">{l.durationDays} j · {l.headcount} pers.</dd></div>
                  <div><dt className="text-stone-400">Prérequis</dt><dd className="font-semibold text-navy">{l.dependsOn?.length ? l.dependsOn.map((d) => p.lots.find((x) => x.id === d)?.label).join(", ") : "—"}</dd></div>
                </dl>
                <div className="mt-3 flex items-center justify-between text-xs"><span className="text-stone-500">Déclaré <b className="text-navy">{l.declaredProgress} %</b></span><span className="text-stone-500">Validé par le Partenaire <b className="text-navy" data-testid={`ent-lot-${l.id}-validated`}>{l.validatedProgress} %</b></span></div>
                <div className="mt-1.5"><Bar value={l.validatedProgress} ghost={l.declaredProgress} color={TERRE} /></div>
              </li>
            ))}
          </ul>
        </div>
      </Card>
      <div className="mt-4 grid gap-4 lg:grid-cols-5">
        <Card className="p-5 lg:col-span-3" testid="ent-planning">
          <SectionTitle>Mon planning</SectionTitle>
          <Gantt project={p} color={TERRE} filterCompany={companyId} testid="ent-gantt" />
          <p className="mt-2 text-[11px] text-stone-400">Vous ne voyez que vos interventions et leurs prérequis.</p>
        </Card>
        <Card className="p-5 lg:col-span-2" testid="ent-declare">
          <SectionTitle>Déclarer un avancement</SectionTitle>
          <form onSubmit={submit} className="space-y-3">
            <Field label="Lot"><select data-testid="ent-declare-lot" className={inputCls} value={form.lotId} onChange={(e) => setForm({ ...form, lotId: e.target.value })}>{myLots.map((l) => <option key={l.id} value={l.id}>{l.label}</option>)}</select></Field>
            <Field label="Avancement (%)"><input data-testid="ent-declare-pct" type="number" min="0" max="100" className={inputCls} value={form.declared} onChange={(e) => setForm({ ...form, declared: e.target.value })} placeholder="50" /></Field>
            <Field label="Commentaire"><input data-testid="ent-declare-comment" className={inputCls} value={form.comment} onChange={(e) => setForm({ ...form, comment: e.target.value })} placeholder="Travaux réalisés à 50 %." /></Field>
            <Field label="Photos (nombre, simulé)"><input data-testid="ent-declare-photos" type="number" min="0" max="10" className={inputCls} value={form.photos} onChange={(e) => setForm({ ...form, photos: e.target.value })} /></Field>
            <TBtn type="submit" testid="ent-declare-submit" className="w-full">Déclarer — à vérifier par le Partenaire</TBtn>
          </form>
          <ul className="mt-4 space-y-1.5 text-xs" data-testid="ent-declarations">
            {p.progressDeclarations.filter((d) => d.companyId === companyId).map((d) => (
              <li key={d.id} className="flex items-center justify-between gap-2"><span>{fmtDate(d.date)} · {p.lots.find((l) => l.id === d.lotId)?.label} · déclaré {d.declared} %{d.validated != null && d.validated !== d.declared && ` → validé ${d.validated} %`}</span><Chip status={d.status} /></li>
            ))}
          </ul>
        </Card>
      </div>
      <div className="mt-4"><EchangesTab project={p} role="entreprise" companyId={companyId} /></div>
    </div>
  );
};

// ---------- Devis ----------
const QUOTE_STATUS_LABELS = { a_examiner: "En examen", retenu: "Présélectionné", accepte: "Retenu", preparation: "Retenu", refuse: "Non retenu" };

const NotificationsBanner = () => {
  const engine = useEngine();
  const [notifications, setNotifications] = useState([]);
  useEffect(() => { engine.companyNotifications().then(setNotifications).catch(() => {}); }, [engine]);
  const unread = notifications.filter((n) => !n.read);
  if (unread.length === 0) return null;
  const dismiss = async (id) => { engine.markCompanyNotificationRead(id); setNotifications((list) => list.map((n) => (n.id === id ? { ...n, read: true } : n))); };
  return (
    <div className="mb-4 space-y-2">
      {unread.map((n) => (
        <div key={n.id} data-testid={`ent-notification-${n.id}`} className="flex items-start gap-3 rounded-2xl p-4 text-sm" style={{ backgroundColor: "#F3E9DE", color: TERRE }}>
          <Bell className="mt-0.5 h-4 w-4 shrink-0" />
          <p className="flex-1 leading-relaxed">{n.message}</p>
          <button data-testid={`ent-notification-dismiss-${n.id}`} onClick={() => dismiss(n.id)} aria-label="Marquer comme lu" className="rounded-full p-1 opacity-60 hover:opacity-100"><X className="h-4 w-4" /></button>
        </div>
      ))}
    </div>
  );
};

const Devis = ({ companyId }) => {
  const engine = useEngine();
  const backend = engine.mode === "BACKEND";
  const p = engine.project(REF);
  const empty = { lotId: "", number: "", date: today(), amountHT: "", tva: 20, durationDays: "", headcount: "", availableFrom: "", comment: "", prerequisites: "", file: "" };
  const [f, setF] = useState(empty);
  const [uploading, setUploading] = useState(false);
  const [requests, setRequests] = useState([]);
  const [respondingId, setRespondingId] = useState(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const loadRequests = useCallback(() => {
    if (!backend) return;
    engine.companyQuoteRequests().then(setRequests).catch(() => toast.error("Impossible de charger les demandes de devis."));
  }, [backend, engine]);
  useEffect(() => { loadRequests(); }, [loadRequests]);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const doc = await engine.uploadPrivateFile(REF, file, "quote", f.lotId || "pending", "PARTNER,COMPANY");
      setF((current) => ({ ...current, file: file.name, fileId: doc.id }));
      toast.success("PDF déposé dans le stockage privé.");
    } catch (err) { toast.error(err.response?.data?.detail || "Échec du téléversement"); }
    finally { setUploading(false); }
  };

  const submitFree = (e) => {
    e.preventDefault();
    if (!f.file || !f.number || !f.date || !f.lotId || !f.amountHT || !f.durationDays) return toast.error("PDF, numéro, date, lot, montant HT et durée des travaux sont obligatoires.");
    if (engine.depositQuote(REF, companyId, { ...f, amountHT: Number(f.amountHT), tva: Number(f.tva), durationDays: Number(f.durationDays), headcount: Number(f.headcount) || 1 })) {
      toast.success("Devis déposé — le Partenaire va l'examiner."); setF(empty);
    }
  };

  const submitRequest = (e, requestId) => {
    e.preventDefault();
    if (!f.fileId || !f.number || !f.date || !f.amountHT || !f.durationDays) return toast.error("PDF, numéro, date, montant HT et durée des travaux sont obligatoires.");
    engine.respondQuoteRequest(requestId, {
      file_id: f.fileId, number: f.number, date: f.date, amount_ht: Number(f.amountHT), tva: Number(f.tva),
      duration_days: Number(f.durationDays), headcount: Number(f.headcount) || 1, available_from: f.availableFrom || null,
      constraints: f.comment, prerequisites: f.prerequisites,
    });
    toast.success("Devis envoyé au Partenaire.");
    setF(empty); setRespondingId(null);
    setTimeout(loadRequests, 700);
  };

  if (!backend) {
    return (
      <div data-testid="ent-devis">
        <Header title="Mes devis" sub="Déposez vos devis avec la durée d'intervention : elle alimente le planning du chantier." />
        <div className="grid gap-4 lg:grid-cols-2">
          <Card className="p-5" testid="ent-quote-form">
            <SectionTitle>Déposer un devis</SectionTitle>
            <form onSubmit={submitFree} className="space-y-3">
              <Field label="PDF du devis *"><div className="flex gap-2"><input data-testid="quote-file" className={inputCls} placeholder="devis-plomberie.pdf" value={f.file} onChange={set("file")} /><TBtn type="button" variant="ghost" onClick={() => setF({ ...f, file: `devis-${Date.now().toString(36)}.pdf` })} testid="quote-file-sim"><Upload className="h-4 w-4" /></TBtn></div></Field>
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Numéro *"><input data-testid="quote-number" className={inputCls} value={f.number} onChange={set("number")} /></Field>
                <Field label="Date *"><input data-testid="quote-date" type="date" className={inputCls} value={f.date} onChange={set("date")} /></Field>
              </div>
              <Field label="Lot *"><select data-testid="quote-lot" className={inputCls} value={f.lotId} onChange={set("lotId")}><option value="">Choisir…</option>{p.lots.map((l) => <option key={l.id} value={l.id}>{l.label}</option>)}</select></Field>
              <div className="grid gap-3 sm:grid-cols-3">
                <Field label="Montant HT *"><input data-testid="quote-ht" type="number" className={inputCls} value={f.amountHT} onChange={set("amountHT")} /></Field>
                <Field label="TVA %"><input data-testid="quote-tva" type="number" className={inputCls} value={f.tva} onChange={set("tva")} /></Field>
                <Field label="Montant TTC"><input readOnly className={`${inputCls} bg-stone-50`} value={f.amountHT ? euro(Number(f.amountHT) * (1 + Number(f.tva) / 100)) : ""} data-testid="quote-ttc" /></Field>
              </div>
              <div className="grid gap-3 sm:grid-cols-3">
                <Field label="Durée (jours ouvrés) *"><input data-testid="quote-duration" type="number" min="1" className={inputCls} value={f.durationDays} onChange={set("durationDays")} placeholder="12" /></Field>
                <Field label="Effectif prévu"><input data-testid="quote-headcount" type="number" min="1" className={inputCls} value={f.headcount} onChange={set("headcount")} placeholder="2" /></Field>
                <Field label="Disponible à partir du"><input data-testid="quote-available" type="date" className={inputCls} value={f.availableFrom} onChange={set("availableFrom")} /></Field>
              </div>
              <Field label="Commentaire / contraintes"><input data-testid="quote-comment" className={inputCls} value={f.comment} onChange={set("comment")} /></Field>
              <Field label="Prérequis"><input data-testid="quote-prereq" className={inputCls} value={f.prerequisites} onChange={set("prerequisites")} /></Field>
              <TBtn type="submit" testid="quote-submit" className="w-full">Déposer le devis</TBtn>
            </form>
          </Card>
          <Card className="p-5" testid="ent-quotes">
            <SectionTitle>Mes devis sur ce chantier</SectionTitle>
            <ul className="divide-y divide-stone-100 text-sm">
              {p.quotes.filter((q) => q.companyId === companyId).map((q) => (
                <li key={q.id} data-testid={`ent-quote-${q.id}`} className="flex items-center justify-between gap-2 py-3"><span><b className="text-navy">{p.lots.find((l) => l.id === q.lotId)?.label}</b> · {q.number}<br /><span className="text-xs text-stone-500">{euro(q.amountHT)} HT · {q.durationDays} j · {q.headcount} pers.</span></span><Chip status={q.status} testid={`ent-quote-${q.id}-status`} /></li>
              ))}
            </ul>
          </Card>
        </div>
      </div>
    );
  }

  const pending = requests.filter((r) => r.status === "pending");
  const answered = requests.filter((r) => r.status === "answered");
  const won = answered.filter((r) => ["accepte", "preparation"].includes(r.quote_status));

  return (
    <div data-testid="ent-devis">
      <Header title="Mes devis" sub="Répondez aux demandes de devis reçues du Partenaire : le dossier et le lot sont déjà renseignés, aucune ressaisie." />
      <NotificationsBanner />
      <Card className="p-5" testid="ent-quote-requests">
        <SectionTitle right={<Pill tone="copper">{pending.length} en attente</Pill>}>Demandes de devis reçues</SectionTitle>
        {pending.length === 0 && <Placeholder testid="ent-quote-requests-empty">Aucune demande de devis en attente.</Placeholder>}
        <ul className="space-y-4">
          {pending.map((r) => (
            <li key={r.id} data-testid={`ent-request-${r.id}`} className="rounded-xl border p-4 text-sm" style={{ borderColor: "#EADFD3" }}>
              <p className="font-mono text-[11px] font-bold" style={{ color: TERRE }}>Dossier {r.project_reference}</p>
              <p className="font-semibold text-navy">{r.lot_label}</p>
              {r.comment && <p className="mt-1 text-xs italic text-stone-500">« {r.comment} »</p>}
              {respondingId !== r.id ? (
                <TBtn className="mt-3 px-3 py-1.5 text-xs" testid={`ent-request-${r.id}-respond`} onClick={() => { setRespondingId(r.id); setF(empty); }}>Répondre à cette demande</TBtn>
              ) : (
                <form onSubmit={(e) => submitRequest(e, r.id)} className="mt-3 space-y-3">
                  <Field label="PDF du devis *"><input data-testid={`ent-request-${r.id}-file`} type="file" accept="application/pdf" className={inputCls} disabled={uploading} onChange={(e) => upload(e.target.files?.[0])} />{f.file && <p className="mt-1 text-xs font-semibold text-sage">{f.file} · stockage privé prêt</p>}</Field>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <Field label="Numéro *"><input data-testid={`ent-request-${r.id}-number`} className={inputCls} value={f.number} onChange={set("number")} /></Field>
                    <Field label="Date *"><input data-testid={`ent-request-${r.id}-date`} type="date" className={inputCls} value={f.date} onChange={set("date")} /></Field>
                  </div>
                  <div className="grid gap-3 sm:grid-cols-3">
                    <Field label="Montant HT *"><input data-testid={`ent-request-${r.id}-ht`} type="number" className={inputCls} value={f.amountHT} onChange={set("amountHT")} /></Field>
                    <Field label="Durée (j ouvrés) *"><input data-testid={`ent-request-${r.id}-duration`} type="number" min="1" className={inputCls} value={f.durationDays} onChange={set("durationDays")} /></Field>
                    <Field label="Démarrage possible"><input data-testid={`ent-request-${r.id}-available`} type="date" className={inputCls} value={f.availableFrom} onChange={set("availableFrom")} /></Field>
                  </div>
                  <Field label="Commentaire"><input data-testid={`ent-request-${r.id}-comment`} className={inputCls} value={f.comment} onChange={set("comment")} /></Field>
                  <div className="flex gap-2">
                    <TBtn type="submit" testid={`ent-request-${r.id}-submit`}>Envoyer mon devis</TBtn>
                    <TBtn type="button" variant="ghost" testid={`ent-request-${r.id}-cancel`} onClick={() => { setRespondingId(null); setF(empty); }}>Annuler</TBtn>
                  </div>
                </form>
              )}
            </li>
          ))}
        </ul>
      </Card>
      {won.length > 0 && (
        <Card className="mt-4 p-5" testid="ent-markets">
          <SectionTitle right={<Trophy className="h-4 w-4" style={{ color: TERRE }} />}>Mes marchés CozyHome</SectionTitle>
          <ul className="divide-y divide-stone-100 text-sm">
            {won.map((r) => (
              <li key={r.id} data-testid={`ent-market-${r.id}`} className="flex items-center justify-between gap-2 py-3">
                <span><b className="text-navy">Dossier {r.project_reference}</b> · {r.lot_label} · {euro(r.quote_amount_ht)} HT</span>
                <Chip status="accepte" label="Marché attribué" />
              </li>
            ))}
          </ul>
        </Card>
      )}
      <Card className="mt-4 p-5" testid="ent-quotes">
        <SectionTitle>Tous mes devis envoyés</SectionTitle>
        {answered.length === 0 && <Placeholder testid="ent-quotes-empty">Aucun devis envoyé pour le moment.</Placeholder>}
        <ul className="divide-y divide-stone-100 text-sm">
          {answered.map((r) => (
            <li key={r.id} data-testid={`ent-quote-${r.quote_id}`} className="flex items-center justify-between gap-2 py-3">
              <span><b className="text-navy">Dossier {r.project_reference}</b> · {r.lot_label}<br /><span className="text-xs text-stone-500">{euro(r.quote_amount_ht)} HT</span></span>
              <Chip status={r.quote_status} label={QUOTE_STATUS_LABELS[r.quote_status] || r.quote_status} testid={`ent-quote-${r.quote_id}-status`} />
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
};

// ---------- Observations ----------
const OBS_VISIBLE_STATUSES = ["a_traiter", "en_cours", "a_verifier", "cloture"];
const Observations = ({ companyId }) => {
  const engine = useEngine();
  const p = engine.project(REF);
  const mine = p.observations.filter((o) => o.companyId === companyId && OBS_VISIBLE_STATUSES.includes(o.status));
  const [after, setAfter] = useState({});
  return (
    <div data-testid="ent-observations">
      <Header title="Observations" sub="Les signalements qui vous sont transmis. Indiquez « C'est fait » : le Partenaire vérifie ensuite." />
      {mine.length === 0 && <Placeholder>Aucun signalement transmis sur vos lots.</Placeholder>}
      <ul className="grid gap-3 md:grid-cols-2">
        {mine.map((o) => (
          <Card key={o.id} testid={`ent-obs-${o.id}`} className="p-4 text-sm">
            <div className="flex items-start justify-between gap-2"><div><p className="font-semibold text-navy">{o.title}</p><p className="text-xs text-stone-500">{p.lots.find((l) => l.id === o.lotId)?.label} · {fmtDate(o.date)}</p></div><Chip status={o.status} testid={`ent-obs-${o.id}-status`} /></div>
            {o.comment && <p className="mt-1 text-xs italic text-stone-500">« {o.comment} »</p>}
            {o.photoIds?.length > 0 && <div className="mt-2 flex flex-wrap gap-2">{o.photoIds.map((pid) => <PhotoThumb key={pid} id={pid} mode={engine.mode} testid={`ent-obs-${o.id}-photo`} />)}</div>}
            <div className="mt-2"><History items={o.history} /></div>
            {["a_traiter", "en_cours"].includes(o.status) && (
              <div className="mt-3 space-y-2 rounded-xl bg-[#FAF6F1] p-3">
                <PhotoField engine={engine} project={p} lotId={o.lotId} onChange={(photoIds) => setAfter({ ...after, [o.id]: photoIds })} testid={`ent-obs-${o.id}-after-photo-field`} />
                <div className="flex gap-2">
                  {o.status === "a_traiter" && <TBtn variant="ghost" className="px-3 py-1.5 text-xs" testid={`ent-obs-${o.id}-treat`} onClick={() => engine.treatObservation(REF, companyId, o.id, false)}>Prendre en charge</TBtn>}
                  <TBtn className="px-3 py-1.5 text-xs" testid={`ent-obs-${o.id}-done`} onClick={() => engine.treatObservation(REF, companyId, o.id, true, after[o.id] || [])}><CheckCircle2 className="h-3.5 w-3.5" /> C'est fait</TBtn>
                </div>
              </div>
            )}
          </Card>
        ))}
      </ul>
    </div>
  );
};

// ---------- Documents & assurances ----------
const Documents = ({ companyId }) => {
  const engine = useEngine();
  const c = engine.company(companyId);
  const comp = engine.compliance(companyId);
  const [f, setF] = useState({ type: "decennale", validFrom: today(), validTo: "" });
  const [file, setFile] = useState(null);
  const types = [...new Set([...(c.requiredDocs || []), ...c.documents.map((d) => d.type)])];
  return (
    <div data-testid="ent-documents">
      <Header title="Documents & assurances" sub="Vos documents professionnels, versionnés. Les exigences dépendent de votre activité." />
      <Card className="p-5" testid="ent-doc-status">
        <div className="flex flex-wrap items-center justify-between gap-2"><SectionTitle>Conformité pour intervenir</SectionTitle><ComplianceBadge companyId={companyId} testid="ent-doc-compliance" /></div>
        <ul className="divide-y divide-stone-100">
          {types.map((t) => {
            const versions = c.documents.filter((d) => d.type === t).sort((a, b) => b.version - a.version);
            const cur = versions[0];
            const expired = cur && cur.validTo && cur.validTo < today();
            return (
              <li key={t} data-testid={`ent-doc-${t}`} className="py-3 text-sm">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="font-semibold text-navy">{COMPANY_DOC_TYPES[t]} {c.requiredDocs.includes(t) && <span className="text-[10px] font-bold uppercase text-stone-400">requis</span>}</span>
                  <Chip status={!cur ? "a_fournir" : expired ? "expire" : cur.status} testid={`ent-doc-${t}-status`} />
                </div>
                <ul className="mt-1.5 space-y-0.5 text-xs text-stone-500">
                  {versions.map((v) => (
                    <li key={v.id} className="flex flex-wrap items-center gap-2"><span className="font-mono">v{v.version}</span> {v.file} · déposé {fmtDate(v.depositedAt)} · valide {fmtDate(v.validFrom)} → {fmtDate(v.validTo)} <Chip status={v.validTo && v.validTo < today() && v.status === "valide" ? "expire" : v.status} />
                      {v.status === "en_verification" && <button data-testid={`ent-doc-${v.id}-verify`} onClick={() => engine.verifyDocument(REF, companyId, v.id, true)} className="rounded-full bg-[#292D2B] px-2 py-0.5 text-[10px] font-bold text-white">Simuler vérification Central</button>}
                    </li>
                  ))}
                </ul>
              </li>
            );
          })}
        </ul>
      </Card>
      <Card className="mt-4 p-5" testid="ent-doc-upload">
        <SectionTitle>Déposer un nouveau document (nouvelle version)</SectionTitle>
        <form className="grid gap-3 sm:grid-cols-4" onSubmit={async (e) => { e.preventDefault(); if (!f.validTo) return toast.error("Indiquez la date de fin de validité."); if (engine.mode === "BACKEND") { if (!file) return toast.error("Sélectionnez le PDF du document."); try { await engine.uploadCompanyDocument(file, f.type, f.validFrom, f.validTo); setFile(null); toast.success("Document déposé — en vérification."); } catch (err) { toast.error(err.response?.data?.detail || "Échec du dépôt"); } } else if (engine.uploadDocument(REF, companyId, f.type, f.validFrom, f.validTo)) toast.success("Document déposé — en vérification. Les copies archivées dans vos chantiers sont conservées."); }}>
          <Field label="Type"><select data-testid="ent-upload-type" className={inputCls} value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })}>{Object.entries(COMPANY_DOC_TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></Field>
          <Field label="Début de validité"><input data-testid="ent-upload-from" type="date" className={inputCls} value={f.validFrom} onChange={(e) => setF({ ...f, validFrom: e.target.value })} /></Field>
          <Field label="Fin de validité"><input data-testid="ent-upload-to" type="date" className={inputCls} value={f.validTo} onChange={(e) => setF({ ...f, validTo: e.target.value })} /></Field>
          {engine.mode === "BACKEND" && <Field label="PDF"><input data-testid="ent-upload-file" type="file" accept="application/pdf" className={inputCls} onChange={(e) => setFile(e.target.files?.[0] || null)} /></Field>}
          <div className="flex items-end"><TBtn type="submit" testid="ent-upload-submit" className="w-full"><Upload className="h-4 w-4" /> Déposer</TBtn></div>
        </form>
        {!comp.ok && <p className="mt-3 text-xs text-red-700">Tant que les documents requis ne sont pas valides, vous n'êtes pas autorisé à démarrer l'intervention concernée.</p>}
      </Card>
    </div>
  );
};

// ---------- Factures ----------
const Factures = ({ companyId }) => {
  const engine = useEngine();
  const p = engine.project(REF);
  const myLots = p.lots.filter((l) => l.companyId === companyId);
  const empty = { lotId: myLots[0]?.id || "", type: "situation", number: "", date: today(), amountHT: "", comment: "", file: "", version: 1 };
  const [f, setF] = useState(empty);
  const [uploading, setUploading] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const mine = p.invoices.filter((i) => i.companyId === companyId);
  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const doc = await engine.uploadPrivateFile(REF, file, "invoice", f.lotId || "pending", "PARTNER,COMPANY");
      setF((current) => ({ ...current, file: file.name, fileId: doc.id }));
      toast.success("PDF déposé dans le stockage privé.");
    } catch (err) { toast.error(err.response?.data?.detail || "Échec du téléversement"); }
    finally { setUploading(false); }
  };
  const submit = (e) => {
    e.preventDefault();
    if (!f.file || !f.number || !f.amountHT || !f.lotId) return toast.error("PDF, numéro, lot et montant HT sont obligatoires.");
    if (engine.depositInvoice(REF, companyId, { ...f, amountHT: Number(f.amountHT), version: Number(f.version) })) { toast.success("Facture déposée — à vérifier par le Partenaire. Non visible du client pour l'instant."); setF(empty); }
  };
  return (
    <div data-testid="ent-factures">
      <Header title="Factures & situations" sub="Entreprise → Partenaire → Client → paiement déclaré → paiement confirmé." />
      <div className="grid gap-4 lg:grid-cols-2">
        <Card className="p-5" testid="ent-invoice-form">
          <SectionTitle>Déposer une facture / situation</SectionTitle>
          <form onSubmit={submit} className="space-y-3">
            <Field label="PDF *"><div className="flex gap-2">{engine.mode === "BACKEND" ? <input data-testid="inv-file" type="file" accept="application/pdf" className={inputCls} disabled={uploading} onChange={(e) => upload(e.target.files?.[0])} /> : <><input data-testid="inv-file" className={inputCls} value={f.file} onChange={set("file")} placeholder="facture.pdf" /><TBtn type="button" variant="ghost" testid="inv-file-sim" onClick={() => setF({ ...f, file: `facture-${Date.now().toString(36)}.pdf` })}><Upload className="h-4 w-4" /></TBtn></>}</div>{engine.mode === "BACKEND" && f.file && <p data-testid="inv-file-ready" className="mt-1 text-xs font-semibold text-sage">{f.file} · stockage privé prêt</p>}</Field>
            <div className="grid gap-3 sm:grid-cols-3">
              <Field label="Type"><select data-testid="inv-type" className={inputCls} value={f.type} onChange={set("type")}>{Object.entries(INVOICE_TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></Field>
              <Field label="Numéro *"><input data-testid="inv-number" className={inputCls} value={f.number} onChange={set("number")} /></Field>
              <Field label="Date"><input data-testid="inv-date" type="date" className={inputCls} value={f.date} onChange={set("date")} /></Field>
            </div>
            <div className="grid gap-3 sm:grid-cols-3">
              <Field label="Lot"><select data-testid="inv-lot" className={inputCls} value={f.lotId} onChange={set("lotId")}>{myLots.map((l) => <option key={l.id} value={l.id}>{l.label}</option>)}</select></Field>
              <Field label="Montant HT *"><input data-testid="inv-ht" type="number" className={inputCls} value={f.amountHT} onChange={set("amountHT")} /></Field>
              <Field label="TTC (TVA 20 %)"><input readOnly className={`${inputCls} bg-stone-50`} value={f.amountHT ? euro(Number(f.amountHT) * 1.2) : ""} data-testid="inv-ttc" /></Field>
            </div>
            <Field label="Commentaire"><input data-testid="inv-comment" className={inputCls} value={f.comment} onChange={set("comment")} /></Field>
            <TBtn type="submit" testid="inv-submit" className="w-full">Déposer — à vérifier par le Partenaire</TBtn>
          </form>
        </Card>
        <Card className="p-5" testid="ent-invoices">
          <SectionTitle>Mes factures</SectionTitle>
          <ul className="space-y-3 text-sm">
            {mine.map((i) => (
              <li key={i.id} data-testid={`ent-invoice-${i.id}`} className="rounded-xl border p-3" style={{ borderColor: "#EADFD3" }}>
                <div className="flex items-start justify-between gap-2"><div><p className="font-semibold text-navy">{INVOICE_TYPES[i.type]} — {p.lots.find((l) => l.id === i.lotId)?.label}</p><p className="text-xs text-stone-500">{i.number} · v{i.version} · {fmtDate(i.date)} · {euro(i.amountHT)} HT / {euro(i.amountTTC)} TTC</p></div><Chip status={i.status} testid={`ent-invoice-${i.id}-status`} /></div>
                {i.status === "retournee" && <p className="mt-1 text-xs text-red-700">Motif : {i.returnReason}</p>}
                {i.payment && <p className="mt-1 text-xs text-stone-500">Paiement déclaré le {fmtDate(i.payment.date)} ({i.payment.mode}){i.payment.confirmedAt && ` · reçu le ${fmtDate(i.payment.confirmedAt)}`}</p>}
                <div className="mt-2"><History items={i.history} /></div>
                <div className="mt-2 flex gap-2">
                  {i.status === "paiement_declare" && <TBtn className="px-3 py-1.5 text-xs" testid={`ent-invoice-${i.id}-confirm`} onClick={() => engine.confirmPayment(REF, companyId, i.id)}><CheckCircle2 className="h-3.5 w-3.5" /> Paiement reçu</TBtn>}
                  {i.status === "retournee" && <TBtn variant="ghost" className="px-3 py-1.5 text-xs" testid={`ent-invoice-${i.id}-correct`} onClick={() => { setF({ ...empty, lotId: i.lotId, type: i.type, number: `${i.number}-B`, amountHT: i.amountHT, version: i.version + 1, comment: `Version corrigée de ${i.number}`, correctsInvoiceId: i.id, correctionReason: i.returnReason || "Correction demandée par le Partenaire" }); toast.info("Formulaire prérempli : déposez la version corrigée (nouveau document, l'original est conservé)."); }}>Déposer une version corrigée</TBtn>}
                </div>
              </li>
            ))}
          </ul>
        </Card>
      </div>
    </div>
  );
};

const MonEntreprise = ({ companyId }) => {
  const engine = useEngine();
  const c = engine.company(companyId);
  return (
    <div data-testid="ent-mon-entreprise">
      <Header title="Mon entreprise" />
      <Card className="p-5 text-sm">
        {[["Raison sociale", c.name], ["Métier", c.trade], ["Contact", c.contact], ["Téléphone", c.phone], ["Email", c.email], ["Réseau CozyHome", c.network ? "Membre du réseau" : "Entreprise extérieure invitée"], ["Documents requis (paramétrable)", c.requiredDocs.map((t) => COMPANY_DOC_TYPES[t]).join(", ")]].map(([k, v]) => (
          <div key={k} className="flex justify-between gap-4 border-b border-stone-100 py-2.5 last:border-b-0"><span className="text-stone-500">{k}</span><span className="text-right font-semibold text-navy">{v}</span></div>
        ))}
      </Card>
    </div>
  );
};

const Page = ({ C }) => { const { companyId } = useOutlet(); return <C companyId={companyId} />; };

export default function EntrepriseSpace() {
  const [companyId, setCompanyId] = useCompanyId();
  return (
    <Routes>
      <Route element={<Layout companyId={companyId} setCompanyId={setCompanyId} />}>
        <Route index element={<Page C={Chantiers} />} />
        <Route path="devis" element={<Page C={Devis} />} />
        <Route path="observations" element={<Page C={Observations} />} />
        <Route path="documents" element={<Page C={Documents} />} />
        <Route path="factures" element={<Page C={Factures} />} />
        <Route path="mon-entreprise" element={<Page C={MonEntreprise} />} />
      </Route>
    </Routes>
  );
}

export const LOTS_AVAILABLE = LOT_CATALOG;
